import { TUpdateOrganizationFormData } from "@/api/services/admin/organization/dtos/update-organization.dto";
import { useActiveOrgUsers } from "@/api/services/admin/organization";
import { useGetProvinceAndMunicipalityList } from "@/api/services/global/constant";
import { useEnum } from "@/api/services/global/enum";
import { IconOpenInNew } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { SectionCard } from "@/components/elements/SectionCard";
import { Select } from "@/components/elements/Select";
import { findLabelByValue } from "@/utils/object";
import { black, text } from "@/theme/colors";
import { TOption } from "@/types/common";
import { Grid, Stack, Tooltip, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { Control, UseFormGetValues, UseFormSetValue, useWatch } from "react-hook-form";
import { IconOutlineInfo } from "@/modules/irohana-components/assets/icons/IconOutlineInfo";

type Props = {
  control: Control<TUpdateOrganizationFormData>;
  getValues: UseFormGetValues<TUpdateOrganizationFormData>;
  setValue: UseFormSetValue<TUpdateOrganizationFormData>;
  externalId: string;
  status: string;
  organizationType: string;
  // Currently-assigned 担当者. Seeded into the options so an inactive/absent assignee still displays.
  currentPersonInCharge?: { id: number; fullName: string } | null;
};

export const OrganizationEditBasicInfo: FC<Props> = ({
  control,
  getValues,
  setValue,
  externalId,
  status,
  organizationType,
  currentPersonInCharge,
}) => {
  const { isLoading: isLoadingProvincesAndMunicipalities, provinceOptions, getMunicipalityOptionsByProvince } = useGetProvinceAndMunicipalityList();
  const { data: organizationStatuses } = useEnum("EOrganizationStatus");
  const { options: assignableUserOptions } = useActiveOrgUsers();

  // The assignable-users list only returns ACTIVE users, so an assignee that later
  // became inactive won't appear — merge it in to keep the Select showing the current value.
  const personInChargeOptions = useMemo<TOption[]>(() => {
    if (!currentPersonInCharge) return assignableUserOptions;
    const currentValue = String(currentPersonInCharge.id);
    if (assignableUserOptions.some((o) => o.value === currentValue)) return assignableUserOptions;
    return [{ label: currentPersonInCharge.fullName, value: currentValue }, ...assignableUserOptions];
  }, [assignableUserOptions, currentPersonInCharge]);

  const provinceWatchField = useWatch({ control, name: "province" });

  const handleOpenCRM = () => {
    const name = getValues("name");
    const url = `https://crm.zoho.jp/crm/irohana/search?isRelevance=false&modules=Accounts&searchword=${encodeURIComponent(name)}`;
    window.open(url, "_blank");
  };

  const handleOpenSecondCRM = () => {
    const url = `https://crm.zoho.jp/crm/irohana/tab/Accounts/create`;
    window.open(url, "_blank");
  };

  return (
    <SectionCard title="基本情報" titleVariant="head20Bold" maxWidth="100%" titleColor={black[30]}>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Stack>
            <FormField label="組織ID（自動生成）">
              <Input nonControl readonly value={externalId} clearable={false} />
            </FormField>
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <FormField
            label="ステータス"
            required
            extendedHelper={
              <Tooltip
                title="アーカイブまたはデータ削除したい場合は、画面を戻って「アーカイブ」または「データ削除」を実行してください。"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, -10],
                        },
                      },
                    ],
                  },
                }}
              >
                <Stack ml="4px">
                  <IconOutlineInfo color={text.secondary} width={16} height={16} />
                </Stack>
              </Tooltip>
            }
          >
            <Input nonControl readonly value={findLabelByValue(organizationStatuses, status)} clearable={false} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="name">
            <Input />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField label="機関種別">
            <Select enumName="EOrganizationType" value={organizationType} readonly />
          </FormField>
        </Grid>

        <Grid item xs={4}>
          <FormField control={control} name="province">
            <Select
              onChange={() => {
                setValue("municipality", "");
              }}
              options={provinceOptions}
              loading={isLoadingProvincesAndMunicipalities}
            />
          </FormField>
        </Grid>
        <Grid item xs={4}>
          <FormField control={control} name="municipality">
            <Select
              readonly={!provinceWatchField}
              options={provinceWatchField ? getMunicipalityOptionsByProvince(provinceWatchField) : []}
              loading={isLoadingProvincesAndMunicipalities}
            />
          </FormField>
        </Grid>
        <Grid item xs={4}>
          <FormField control={control} name="personInChargeId">
            <Select options={personInChargeOptions} />
          </FormField>
        </Grid>

        <Grid item xs={12}>
          <FormField control={control} name="crmLink">
            <Stack
              direction="row"
              alignItems="center"
              gap="24px"
              sx={{
                ".MuiTextField-root": {
                  flex: 1,
                },
              }}
            >
              <Input />
              <Stack gap="12px" flexDirection="row">
                <Button
                  onClick={handleOpenCRM}
                  variant="ghost"
                  size="sm"
                  sx={{
                    cursor: "pointer",
                    height: "33px !important",
                  }}
                >
                  <Typography variant="body14Semi" color={text.secondary} pr="8px">
                    CRM会社検索
                  </Typography>
                  <IconOpenInNew />
                </Button>
                <Button
                  onClick={handleOpenSecondCRM}
                  variant="ghost"
                  size="sm"
                  sx={{
                    cursor: "pointer",
                    height: "33px !important",
                  }}
                >
                  <Typography variant="body14Semi" color={text.secondary} pr="8px">
                    CRM会社登録
                  </Typography>
                  <IconOpenInNew />
                </Button>
              </Stack>
            </Stack>
          </FormField>
        </Grid>
      </Grid>
    </SectionCard>
  );
};
